/**
 * learnerIdentity — light sign-in for learners.
 *
 * Captures email + display name on first visit (no password, no token).
 * Derives a deterministic learner_id from the email so the same person
 * always lands on the same row in the analytics roster — even if they
 * sign in from a different device. ID format:
 *
 *     L_<hex8>     // first 8 hex chars of sha-256(lowercased email)
 *
 * Persistence: localStorage. We re-sync to the backend on each load
 * so wf_users always has the latest display name without needing a
 * separate "update profile" UI.
 *
 * NOT a real auth boundary. The master-code gate on the analytics page
 * is the only access control; everything else is informational identity
 * (so we can attribute activity to a name in the dashboard).
 */

const LS_KEY = "maldek_identity_v1";
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Deterministic learner_id from email — uses Web Crypto so we avoid
 * pulling a hashing lib into the bundle. Result is identical to what
 * the backend would produce via hashlib if we ever need to reconcile.
 */
export async function deriveLearnerId(email) {
  const enc = new TextEncoder().encode(String(email || "").toLowerCase().trim());
  const buf = await crypto.subtle.digest("SHA-256", enc);
  const hex = Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, "0")).join("");
  return `L_${hex.slice(0, 8)}`;
}

export function getIdentity() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o.email || !o.display_name || !o.learner_id) return null;
    return o;
  } catch {
    return null;
  }
}

export function clearIdentity() {
  try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent("maldek-identity-change", { detail: null })); } catch { /* noop */ }
}

export async function setIdentity({ email, display_name, cohort_id, institution_id }) {
  const learner_id = await deriveLearnerId(email);
  const identity = {
    email: String(email).toLowerCase().trim(),
    display_name: String(display_name).trim(),
    learner_id,
    cohort_id: cohort_id || null,
    institution_id: institution_id || null,
    signed_in_at: new Date().toISOString(),
  };
  try { localStorage.setItem(LS_KEY, JSON.stringify(identity)); } catch { /* noop */ }
  // Notify same-tab listeners (storage events only fire in OTHER tabs).
  // RequireIdentity and the header pill listen for this so the gate
  // lifts immediately after sign-in.
  try { window.dispatchEvent(new CustomEvent("maldek-identity-change", { detail: identity })); } catch { /* noop */ }
  // Best-effort backend sync — failure is non-fatal (the identity is
  // still usable locally; we'll retry on next page load).
  try {
    await fetch(`${API}/api/learner-identity/upsert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(identity),
    });
  } catch { /* noop */ }
  return identity;
}

/**
 * Attach the signed-in learner_id to an outgoing fetch request via
 * the X-Maldek-Learner header. Routes that auto-log activity look
 * for this header and stamp the resulting event with the user_id.
 */
export function withIdentityHeaders(extra = {}) {
  const id = getIdentity();
  if (!id) return extra;
  return { ...extra, "X-Maldek-Learner": id.learner_id };
}
